import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, type Signal, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { DiagnosticRating, MaloupeLocale } from '@malou-io/package-utils';

import type { Diagnostic } from ':shared/models';

import { PartnerThemeContext } from ':core/context/partner-theme.context';
import { DiagnosticCompetitorsComponent } from ':modules/diagnostic-competitors/diagnostic-competitors.component';
import { DiagnosticInconsistenciesComponent } from ':modules/diagnostic-inconsistencies/diagnostic-inconsistencies.component';
import { DiagnosticScoreComponent } from ':modules/diagnostic-score/diagnostic-score.component';
import { RestaurantDiagnosticKeywordsComponent } from ':modules/restaurant-diagnostic-keywords/restaurant-diagnostic-keywords.component';
import { RestaurantInformationComponent } from ':modules/restaurant-information/restaurant-information.component';
import { PartnerHeaderComponent, SemanticAnalysisComponent } from ':shared/components';
import { SocialNetworkDiagnosticResultComponent } from ':shared/components/social-network-diagnostic-result/social-network-diagnostic-result.component';
import {
    AVERAGE_KEYWORD_POSITION,
    BAD_KEYWORD_POSITION,
    EXPERT_DIAGNOSTIC_QUICK_CALL_EN,
    EXPERT_DIAGNOSTIC_QUICK_CALL_FR,
    GOOD_KEYWORD_POSITION,
    MINIMUM_POST_COUNT_RECOMMENDATION,
} from ':shared/constants';
import { Icon, MaloupePlatformKey } from ':shared/enums';
import { SvgIcon } from ':shared/enums/svg-icon.enum';
import { Illustration, IllustrationPathResolverPipe, ImagePathResolverPipe, SentenceCasePipe } from ':shared/pipes';

@Component({
    selector: 'app-restaurant-diagnostic-result',
    imports: [
        NgClass,
        NgTemplateOutlet,
        DiagnosticCompetitorsComponent,
        DiagnosticInconsistenciesComponent,
        DiagnosticScoreComponent,
        PartnerHeaderComponent,
        RestaurantDiagnosticKeywordsComponent,
        RestaurantInformationComponent,
        SemanticAnalysisComponent,
        MatIconModule,
        MatTableModule,
        TranslateModule,
        IllustrationPathResolverPipe,
        ImagePathResolverPipe,
        SentenceCasePipe,
        SocialNetworkDiagnosticResultComponent,
    ],
    templateUrl: './restaurant-diagnostic-result.component.html',
    styleUrl: './restaurant-diagnostic-result.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RestaurantDiagnosticResultComponent {
    readonly diagnostic = input<Diagnostic | null>(null);
    readonly publicDiagnosticUrl = input<string | null>(null);
    readonly isMalouUser = input<boolean>(false);
    readonly receiverEmail = input<string | null>(null);

    private readonly _translateService = inject(TranslateService);
    private readonly _partnerThemeContext = inject(PartnerThemeContext);

    readonly SvgIcon = SvgIcon;
    readonly PlatformKey = MaloupePlatformKey;
    readonly Illustration = Illustration;
    readonly DiagnosticRating = DiagnosticRating;
    readonly Icon = Icon;

    readonly MINIMUM_POST_COUNT_RECOMMENDATION = MINIMUM_POST_COUNT_RECOMMENDATION;

    readonly isLinkCopied = signal<boolean>(false);
    readonly isMalouRestaurant = computed(() => this.diagnostic()?.isMalouRestaurant ?? false);
    readonly restaurantRating = computed(() => this.diagnostic()?.restaurant.rating);
    readonly locale: MaloupeLocale = this._translateService.currentLang as MaloupeLocale;
    readonly diagnosticDate = computed(() => {
        const createdAt = this.diagnostic()?.createdAt;
        if (!createdAt) {
            return '';
        }
        return new Date(createdAt).toLocaleString(this.locale, { month: 'long', year: 'numeric' });
    });
    readonly partnerTheme = this._partnerThemeContext.activeTheme;

    readonly restaurantAverageRatingForSimilarRestaurants = computed(
        () => this.diagnostic()?.restaurant.averageRatingForSimilarRestaurants
    );

    readonly chosenKeywords = computed(() => this.diagnostic()?.getKeywords() ?? []);
    readonly mappedKeywords = computed(() => {
        const chosenKeywords = this.chosenKeywords();
        return chosenKeywords.map((keyword) => ({
            keyword,
            rating: this._getRatingFromGooglePosition(keyword.googlePosition),
            displayedRating: this._getDisplayedRatingFromGooglePosition(keyword.googlePosition),
            text: keyword.keyword[this.locale],
            restaurantName: this.diagnostic()?.restaurant.name ?? '',
            address: this.diagnostic()?.restaurant.address.formattedAddress ?? '',
            placeId: this.diagnostic()?.placeId ?? '',
        }));
    });
    readonly keywordsDiagnosticRating = computed(() => this.diagnostic()?.getKeywordsRating() ?? DiagnosticRating.BAD);

    readonly instagramPage = computed(() => this.diagnostic()?.instagramPage);
    readonly instagramDiagnosticRating: Signal<
        | {
              followersDiagnosticRating: DiagnosticRating;
              likesDiagnosticRating: DiagnosticRating;
              postCountDiagnosticRating: DiagnosticRating;
          }
        | undefined
    > = computed(() => this.diagnostic()?.getInstagramRating());
    readonly doesRestaurantHasInstagramData = computed(() => !!this.diagnostic()?.instagramPage?.name);

    readonly facebookPage = computed(() => this.diagnostic()?.facebookPage);
    readonly facebookDiagnosticRating: Signal<
        | {
              followersDiagnosticRating: DiagnosticRating;
              likesDiagnosticRating: DiagnosticRating;
              postCountDiagnosticRating: DiagnosticRating;
          }
        | undefined
    > = computed(() => this.diagnostic()?.getFacebookRating());
    readonly doesRestaurantHasFacebookData = computed(() => !!this.diagnostic()?.facebookPage?.name);

    talkToAnExpert(): void {
        const url = this.locale === MaloupeLocale.EN ? EXPERT_DIAGNOSTIC_QUICK_CALL_EN : EXPERT_DIAGNOSTIC_QUICK_CALL_FR;
        window.open(url, '_blank');
    }

    copyLink(): void {
        const publicDiagnosticUrl = this.publicDiagnosticUrl();
        if (publicDiagnosticUrl) {
            navigator.clipboard.writeText(publicDiagnosticUrl).then(() => {
                this.isLinkCopied.set(true);
            });
        }
    }

    private _getRatingFromGooglePosition(googlePosition: number): DiagnosticRating {
        if (BAD_KEYWORD_POSITION.includes(googlePosition)) {
            return DiagnosticRating.BAD;
        }
        if (AVERAGE_KEYWORD_POSITION.includes(googlePosition)) {
            return DiagnosticRating.AVERAGE;
        }
        if (GOOD_KEYWORD_POSITION.includes(googlePosition)) {
            return DiagnosticRating.GOOD;
        }
        return DiagnosticRating.VERY_BAD;
    }

    private _getDisplayedRatingFromGooglePosition(googlePosition: number): string {
        if (googlePosition >= 20) {
            return '+20';
        }
        return googlePosition.toString();
    }
}
